import { Form } from "react-bootstrap";
import { ContractorSearch } from "components/ContractorSearch";
import ContactFields from "../ApplicationContactsWidget/ContactFields";
import { ExistingContact } from "../ApplicationContactsWidget/ExistingContact";
import { getContactName, getReqFields, needsFirstAndLastOrCompany } from "../ApplicationContactsWidget/utils";
import { useLotLabel } from "../ApplicationContactsWidget/useLotLabel";
import { useCustomerContacts } from "components/utils/useCustomerContacts";
import { ContractorData } from "components/utils/contacts";
import { RequireContact } from "../../../../../types/RequireContact";
import { AdditionalContactFormData, AdditionalContactRequirements } from "./types";
import { isContractorContact } from "./utils";
import { isEmpty } from "lodash";
import { ContactInboundData, ContactValidationResult } from "../ApplicationContactsWidget/types";
import { ContactRequirements } from "types/ContactRequirements";

const CONTACT_FIELDS = [
    "firstname",
    "lastname",
    "company",
    "premiseid",
    "meterid",
    "acct_number",
    "address",
    "address_cont",
    "city",
    "state",
    "zip",
    "phone",
    "cell",
    "fax",
    "email",
    "taxid",
    "lot",
];

export const AdditionalContactForm: React.FC<{
    index: number;
    formData?: AdditionalContactFormData;
    errors: ContactValidationResult;
    applicationNumber: string;
    requirements: AdditionalContactRequirements;
    fullContactRequirements: ContactRequirements;
    onUseCustomerContact: (contact: ContactInboundData | undefined) => void;
    onChange: (formData: AdditionalContactFormData) => void;
    onClearForm: () => void;
}> = ({
    index,
    formData,
    errors,
    requirements,
    applicationNumber,
    fullContactRequirements,
    onChange,
    onClearForm,
    onUseCustomerContact,
}) => {
    const [customerContacts = []] = useCustomerContacts(1, 100);
    const lotLabel = useLotLabel();

    const idPrefix = `additional-contact-${index}`;

    const requiredFields = requirements.requiredFields?.split(",").map((f) => f.trim().toLowerCase()) ?? [];
    const showFields = requirements.showFieldNames?.split(",").map((f) => f.trim().toLowerCase()) ?? [];
    const hiddenFields = CONTACT_FIELDS.filter((f) => !showFields.includes(f));

    const showContractorSearch =
        fullContactRequirements.requireContractor &&
        [RequireContact.RequiredFromList, RequireContact.NotRequiredFromList].includes(fullContactRequirements.requireContractor);

    const requiredFLC = getReqFields(requiredFields, formData);

    const isFromStoredContacts = customerContacts.some((c) => c.contactNumber === formData?.storedContactNumber);
    const isFromContractorSearch = isContractorContact(formData);
    const isExisting = (isFromStoredContacts && isEmpty(errors)) || isFromContractorSearch;

    const selectContractor = (contractor: ContractorData) => {
        onChange({
            contactTitle: contractor.contactTitle,
            company: contractor.company ?? undefined,
            firstName: contractor.firstName ?? undefined,
            lastName: contractor.lastName ?? undefined,
            premiseId: contractor.premiseId ?? undefined,
            meterId: contractor.meterId ?? undefined,
            acct_number: contractor.acct_number ?? undefined,
            lot: contractor.lot ?? undefined,
            address: contractor.address ?? undefined,
            address_cont: contractor.address_cont ?? undefined,
            city: contractor.city ?? undefined,
            state: contractor.state ?? undefined,
            zip: contractor.zip ?? undefined,
            phone: contractor.phone ?? undefined,
            cell: contractor.cell ?? undefined,
            fax: contractor.fax ?? undefined,
            email: contractor.email ?? undefined,
            taxId: contractor.taxId ?? undefined,
            origId: contractor.origId ?? undefined,
        });
    };

    return (
        <div style={{ maxWidth: "925px" }} className="border bg-white mb-3 rounded-8 mx-auto">
            <div className="m-3">
                <h6 className="fs-6 fw-bold mb-2">{requirements.title}</h6>

                <div className="d-flex flex-wrap align-items-end justify-content-between gap-3">
                    {customerContacts.length > 0 && (
                        <Form.Group style={{ maxWidth: "320px", minWidth: "300px", flex: 1 }} controlId={`${idPrefix}-storedContact`}>
                            <Form.Label>Choose from existing contact</Form.Label>
                            <Form.Select
                                value={formData?.storedContactNumber ?? ""}
                                onChange={(e) => {
                                    onUseCustomerContact?.(customerContacts?.find((c) => c.contactNumber === e.target.value));
                                }}
                            >
                                <option disabled value="">
                                    Select a contact...
                                </option>
                                {customerContacts.map((c) => (
                                    <option key={c.contactNumber} value={c.contactNumber}>
                                        {getContactName(c)}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    )}

                    {showContractorSearch && (
                        <ContractorSearch applicationNumber={applicationNumber} onSelect={selectContractor} variant="link" />
                    )}
                </div>
            </div>

            {isExisting ? (
                <ExistingContact
                    onClear={onClearForm}
                    onUseCustomerContact={onUseCustomerContact}
                    customerContacts={customerContacts}
                    contact={formData}
                    hiddenFields={hiddenFields}
                    lotLabel={lotLabel}
                />
            ) : (
                <ContactFields
                    idPrefix={idPrefix}
                    formData={formData}
                    requiredFields={[...requiredFields.filter((s) => !["firstname", "lastname", "company"].includes(s)), ...requiredFLC]}
                    hiddenFields={hiddenFields}
                    errors={errors}
                    onChange={(patch: Partial<AdditionalContactFormData>) => onChange({ ...formData, ...patch })}
                    onClearForm={onClearForm}
                    lotLabel={lotLabel}
                    needsFirstAndLastOrCompany={needsFirstAndLastOrCompany(requiredFields)}
                />
            )}
        </div>
    );
};
